import React, { useMemo, useState } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { Box } from 'transfix-ui/core'
import Grid from 'transfix-ui/core/Grid'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { IOutboundRawEdiFile } from 'types/graphqlTypes'
import { ShipmentPendingFilesTable } from './ShipmentPendingFilesTable'

type TListedShipmentPendingFile = Pick<
  IOutboundRawEdiFile,
  | 'id'
  | 'createdAt'
  | 'ediMessageType'
  | 'outboundConnectionConfigId'
  | 'rogersFileDetailUrl'
  | 's3Key'
  | 'shipmentId'
  | 'shipperId'
  | 'ediMetadata'
>

const useShipmentPendingFileStyles = makeStyles(theme => {
  return {
    container: {
      marginTop: theme.spacing(2),
    },
    error: {
      color: brandColors.error1,
    },
  }
})

export interface IShipmentPendingFilesProps {
  shipmentId: string
  onLoadPendingFiles: (shipmentId: string) => void
  onDeleteSelectedFiles: (outboundRawEdiFileIds: string[], shipmentId: string) => Promise<void>
  onUploadSelectedFiles: (
    outboundRawEdiFileIds: string[],
    outboundConnectionConfigId: string,
    shipmentId: string
  ) => Promise<void>
  pendingFiles: TListedShipmentPendingFile[]
  loading: boolean
  errorMessage: string | undefined
  errorBacktrace: string | undefined
}

export const ShipmentPendingFiles = ({
  errorBacktrace,
  errorMessage,
  loading,
  onDeleteSelectedFiles,
  onLoadPendingFiles,
  onUploadSelectedFiles,
  pendingFiles,
  shipmentId,
}: IShipmentPendingFilesProps) => {
  const classes = useShipmentPendingFileStyles()

  const [selectedFiles, setSelectedFiles] = useState<string[]>([])

  const checkBoxChangeHandler = (outboundFileId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedFiles(prevFiles => [...prevFiles, outboundFileId])
    } else {
      setSelectedFiles(prevFiles => prevFiles.filter(id => id !== outboundFileId))
    }
  }

  // Since the mutation for uploading files requires an outbound config ID,
  // we need to know if we have selected multiple outbound config IDs
  // among the set of selected files. The set of selected files must be all
  // share the same outbound config ID if we want to allow the upload button
  // to be clicked
  const allSelectedConfigIds = useMemo(() => {
    return [
      ...new Set(
        pendingFiles
          .filter((file: TListedShipmentPendingFile) => selectedFiles.includes(file.id))
          .map((file: TListedShipmentPendingFile) => file.outboundConnectionConfigId)
      ),
    ]
  }, [pendingFiles, selectedFiles])

  const loadPendingFilesHandler = () => {
    onLoadPendingFiles(shipmentId)
  }

  const uploadFilesHandler = async () => {
    await onUploadSelectedFiles(selectedFiles, allSelectedConfigIds[0], shipmentId)
    setSelectedFiles([])
  }

  const deleteFilesHandler = async () => {
    await onDeleteSelectedFiles(selectedFiles, shipmentId)
    setSelectedFiles([])
  }

  return (
    <Grid container className={classes.container}>
      <Grid item xs={4}>
        <Button
          color='primary'
          size='small'
          data-test='test-list-button'
          onClick={loadPendingFilesHandler}
          disabled={!shipmentId.length}>
          List Pending Files
        </Button>
      </Grid>
      <Grid item xs={4}>
        <Button
          color='primary'
          size='small'
          data-test='test-upload-button'
          onClick={uploadFilesHandler}
          disabled={!selectedFiles.length || allSelectedConfigIds.length > 1}>
          Upload Selected Files
        </Button>
        {allSelectedConfigIds.length > 1 && (
          <Box>For uploading, only select files for one outbound config ID at a time</Box>
        )}
      </Grid>
      <Grid item xs={4}>
        <Button
          color='secondary'
          size='small'
          data-test='test-delete-button'
          onClick={deleteFilesHandler}
          disabled={!selectedFiles.length}>
          Delete Selected Files
        </Button>
      </Grid>
      <Grid item xs={12}>
        {!loading && errorMessage && (
          <Box className={classes.error}>
            <Box>{errorMessage}</Box>
            <Box>{errorBacktrace}</Box>
          </Box>
        )}
      </Grid>
      <Grid item xs={12}>
        <ShipmentPendingFilesTable
          loading={loading}
          pendingFiles={pendingFiles}
          selectedFiles={selectedFiles}
          checkBoxChangeHandler={checkBoxChangeHandler}
        />
      </Grid>
    </Grid>
  )
}
